var __jsx = React.createElement;
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MuscleButton } from "@modules/shared/components/buttons";
import MuscleFlyOverPanel from "@modules/shared/components/fly-over-panel";
import { useTranslations } from "next-intl";
import MuscleDateRangeCalendar from "@modules/shared/components/date-calendars/date-range-calendar";
import MuscleSingleDateCalendar from "@modules/shared/components/date-calendars/single-date-calendar";
import { dateIsAfter, getDateString } from "@lib/util/date-ops";
import { cn } from "@lib/cn";
import moment from "moment";
import MuscleFormDateInput from "../form-date-input";
import APP_CONFIGURATION from "@lib/app.config";
import { BREAKPOINTS } from "@lib/constants";
import CalendarPricesFooter from "../calendar-prices-footer";
import { useMediaQuery } from "react-responsive";
import Icons from "../../../icons";
import CustomInput from "../custom-input";
import InformativeCard from "../cards/informative";
const MuscleCalendarDateInput = _ref => {
  var _dateRangeModeConfigs, _dateRangeModeConfigs2, _memoizedCalendarData, _memoizedCalendarData2, _memoizedCalendarData3;
  let {
    label,
    value = [undefined, undefined],
    onChangeValue,
    dateRangeMode = false,
    dateRangeModeConfigs = {},
    minDate = moment().toDate(),
    maxDate,
    singleDateModeConfigs = {},
    formatDateSelected = date => getDateString(date, false, false),
    calendarAlignment = "right",
    hasErrors,
    errorMessage,
    className = '',
    disclaimerText,
    applyImmediate = false,
    onCurrentShowedMonthChange,
    rangeIdentifier = '*-*',
    disable = false,
    customInputClass,
    dataTestId,
    onClick,
    hideLabel = false
  } = _ref;
  const t = useTranslations();
  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINTS.md - 1
  });
  const {
    0: selectedDates,
    1: setSelectedDates
  } = useState(value);
  const {
    0: calendarOpen,
    1: setCalendarOpen
  } = useState(false);
  const {
    0: textFieldValue,
    1: setTextFieldValue
  } = useState("");
  const {
    0: currentMonth,
    1: setCurrentMonth
  } = useState();
  const datesSeparator = (_dateRangeModeConfigs = dateRangeModeConfigs.datesSeparator) !== null && _dateRangeModeConfigs !== void 0 ? _dateRangeModeConfigs : "/";
  const datesPrefix = (_dateRangeModeConfigs2 = dateRangeModeConfigs.datesPrefix) !== null && _dateRangeModeConfigs2 !== void 0 ? _dateRangeModeConfigs2 : "";
  const calendarFlyOverButtonRef = React.useRef(null);
  const {
    0: memoizedCalendarData,
    1: setMemoizedCalendarData
  } = useState(undefined);
  const {
    0: minPeriod,
    1: setMinPeriod
  } = useState(0);
  const {
    0: maxPeriod,
    1: setMaxPeriod
  } = useState(0);
  const {
    0: lastDates,
    1: setLastDates
  } = useState([undefined, undefined]);
  const {
    0: currentRangeIdentifier,
    1: setCurrentRangeIdentifier
  } = useState('');
  const {
    input_class
  } = APP_CONFIGURATION.config.forms.form_builder;
  const ref = useRef(null);
  const lastValueRef = useRef(value);
  const isUpdatingFromParentRef = useRef(false);
  const getPeriod = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return year * 12 + month;
  };
  useEffect(() => {
    var _lastValueRef$current, _value$, _lastValueRef$current2, _value$2;
    // Only update if the value prop has actually changed and we're not in the middle of updating from parent
    const valueChanged = ((_lastValueRef$current = lastValueRef.current[0]) === null || _lastValueRef$current === void 0 ? void 0 : _lastValueRef$current.getTime()) !== ((_value$ = value[0]) === null || _value$ === void 0 ? void 0 : _value$.getTime()) || ((_lastValueRef$current2 = lastValueRef.current[1]) === null || _lastValueRef$current2 === void 0 ? void 0 : _lastValueRef$current2.getTime()) !== ((_value$2 = value[1]) === null || _value$2 === void 0 ? void 0 : _value$2.getTime());
    if (valueChanged && !isUpdatingFromParentRef.current) {
      lastValueRef.current = value;
      setSelectedDates(value);
    }
  }, [value]);
  useEffect(() => {
    const currentRef = ref.current;
    if (currentRef) {
      currentRef.style.setProperty("--hover-outline-color", APP_CONFIGURATION.colors.primary_main);
    }
  }, []);
  useEffect(() => {
    if (dateRangeMode) {
      setTextFieldValue(getDateRangeTextFieldValue(selectedDates[0], selectedDates[1]));
    } else {
      setTextFieldValue(getSingleDateTextFieldValue(selectedDates[0]));
    }
  }, [selectedDates]);
  const changeMonthCallback = useCallback(function (month, _selectedDates) {
    let force = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (onCurrentShowedMonthChange) {
      if (lastDates[1] === _selectedDates[1] &&
      // not between min and max period
      getPeriod(month) >= minPeriod && getPeriod(month) <= maxPeriod && !force && currentRangeIdentifier === rangeIdentifier) {
        return;
      }
      setCurrentRangeIdentifier(rangeIdentifier);
      onCurrentShowedMonthChange(month, _selectedDates).then(data => {
        var _data$data;
        setMemoizedCalendarData(data);
        if (data && (data === null || data === void 0 || (_data$data = data.data) === null || _data$data === void 0 ? void 0 : _data$data.length) > 0) {
          setMinPeriod(Math.min(...data.data.map(price => price.period)));
          setMaxPeriod(Math.max(...data.data.map(price => price.period)));
          setLastDates([_selectedDates[0], _selectedDates[1]]);
        }
      });
    }
  }, [onCurrentShowedMonthChange, lastDates, minPeriod, maxPeriod, rangeIdentifier]);
  function getLabelIcon(label) {
    return __jsx("div", {
      className: "flex items-center gap-2 bg-transparent"
    }, __jsx("div", {
      className: "mr-4 bg-transparent"
    }, __jsx(Icons.calendar, {
      size: APP_CONFIGURATION.config.inputsIconSize,
      color: APP_CONFIGURATION.colors.globals.input_icon
    })), __jsx("div", null, __jsx("p", {
      className: cn("bg-transparent", customInputClass || input_class),
      style: {
        color: APP_CONFIGURATION.colors.globals.input_text
      }
    }, label)));
  }
  const handleChangeDate = (from, to) => {
    var _selectedDates$, _selectedDates$2;
    // Prevent unnecessary updates if dates haven't changed
    const datesChanged = ((_selectedDates$ = selectedDates[0]) === null || _selectedDates$ === void 0 ? void 0 : _selectedDates$.getTime()) !== (from === null || from === void 0 ? void 0 : from.getTime()) || ((_selectedDates$2 = selectedDates[1]) === null || _selectedDates$2 === void 0 ? void 0 : _selectedDates$2.getTime()) !== (to === null || to === void 0 ? void 0 : to.getTime());
    if (datesChanged) {
      setSelectedDates([from, to]);
      if (applyImmediate || isMobile) {
        // Set flag to prevent useEffect from updating when parent responds
        isUpdatingFromParentRef.current = true;
        onChangeValue({
          from,
          to
        });
        // Reset flag after a short delay to allow parent to update
        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 0);
      }
    }
  };
  const handleFromDateChange = date => {
    if (dateRangeMode) {
      handleChangeDate(date, selectedDates[1]);
    } else {
      handleChangeDate(date, undefined);
    }
  };
  const handleToDateChange = date => {
    handleChangeDate(selectedDates[0], date);
  };
  const getFormattedDateForDisplay = date => {
    if (!date) return "";
    return formatDateSelected(date.toISOString());
  };
  const toggleCalendar = calendarOpen => {
    if (calendarOpen) {
      changeMonthCallback(minDate, selectedDates, true);
    }
    setCalendarOpen(calendarOpen);
  };
  const getDateRangeTextFieldValue = (departure_date, return_date) => {
    if (departure_date) {
      if (!return_date) {
        return "".concat(datesPrefix, " ").concat(getFormattedDateForDisplay(departure_date));
      } else {
        return "".concat(datesPrefix, " ").concat(getFormattedDateForDisplay(departure_date), " ").concat(datesSeparator, " ").concat(getFormattedDateForDisplay(return_date));
      }
    } else {
      return "";
    }
  };
  const getSingleDateTextFieldValue = departure_date => {
    return departure_date ? getFormattedDateForDisplay(departure_date) : "";
  };
  const getDateRangePlaceholder = () => {
    var _dateRangeModeConfigs3;
    return (_dateRangeModeConfigs3 = dateRangeModeConfigs.placeholder) !== null && _dateRangeModeConfigs3 !== void 0 ? _dateRangeModeConfigs3 : t('Shared.SELECT_DATE_RANGE');
  };
  const getSingleDatePlaceholder = () => {
    var _singleDateModeConfig;
    return (_singleDateModeConfig = singleDateModeConfigs.placeholder) !== null && _singleDateModeConfig !== void 0 ? _singleDateModeConfig : t('Shared.SELECT_DATE');
  };
  const fromTextInputMaxDate = useMemo(() => {
    var _selectedDates$3;
    if (selectedDates[1] && maxDate && dateIsAfter(maxDate.toISOString(), selectedDates[1].toISOString())) {
      return maxDate.toISOString();
    }
    return (_selectedDates$3 = selectedDates[1]) === null || _selectedDates$3 === void 0 ? void 0 : _selectedDates$3.toISOString();
  }, [selectedDates[1], maxDate]);
  const toTextInputMinDate = useMemo(() => {
    var _selectedDates$4;
    if (selectedDates[0] && dateIsAfter(minDate.toISOString(), selectedDates[0].toISOString())) {
      return minDate.toISOString();
    }
    return (_selectedDates$4 = selectedDates[0]) === null || _selectedDates$4 === void 0 ? void 0 : _selectedDates$4.toISOString();
  }, [selectedDates[0], minDate]);
  useEffect(() => {
    if (calendarOpen && currentRangeIdentifier !== rangeIdentifier) {
      changeMonthCallback(minDate, selectedDates, true);
    }
  }, [rangeIdentifier, calendarOpen]);
  return __jsx("div", {
    className: cn("flex flex-col w-full", className),
    ref: ref
  }, __jsx("div", {
    className: cn("relative")
  }, __jsx(CustomInput, {
    customInputClass: customInputClass,
    className: "w-full",
    id: "outlined-read-only-input",
    label: label && !disable && !hideLabel ? label : "",
    value: textFieldValue,
    placeholder: dateRangeMode ? getDateRangePlaceholder() : getSingleDatePlaceholder(),
    onClick: () => {
      if (isMobile) {
        onClick === null || onClick === void 0 || onClick();
      } else {
        toggleCalendar(true);
      }
    },
    startAdornment: __jsx(Icons.calendar, {
      size: APP_CONFIGURATION.config.inputsIconSize,
      color: APP_CONFIGURATION.colors.globals.input_icon
    }),
    error: hasErrors,
    disabled: disable
  }), hasErrors && errorMessage && __jsx("p", {
    className: "left-0 -bottom-5  text-xs text-error-main mx-[16px] font-[400]"
  }, errorMessage)), __jsx(MuscleFlyOverPanel, {
    align: calendarAlignment,
    buttonRef: calendarFlyOverButtonRef,
    isOpen: calendarOpen && !disable,
    setIsOpen: toggleCalendar
  }, __jsx("div", {
    className: "flex flex-col p-4 ".concat(isMobile ? 'w-full' : 'w-min')
  }, dateRangeMode ? __jsx(MuscleDateRangeCalendar, {
    onSelect: dateRange => {
      handleChangeDate(dateRange.from, dateRange.to);
      changeMonthCallback(currentMonth !== null && currentMonth !== void 0 ? currentMonth : minDate, [dateRange.from, dateRange.to], true);
    },
    value: {
      from: selectedDates[0],
      to: selectedDates[1]
    },
    minDate: minDate,
    maxDate: maxDate,
    prices: memoizedCalendarData === null || memoizedCalendarData === void 0 ? void 0 : memoizedCalendarData.data,
    onMonthChange: month => {
      setCurrentMonth(month);
      changeMonthCallback(month, selectedDates, true);
    },
    isMobile: isMobile
  }) : __jsx(MuscleSingleDateCalendar, {
    onSelect: date => {
      handleChangeDate(date, undefined);
      changeMonthCallback(currentMonth !== null && currentMonth !== void 0 ? currentMonth : minDate, [date, undefined]);
    },
    value: selectedDates[0],
    minDate: minDate,
    maxDate: maxDate,
    prices: memoizedCalendarData === null || memoizedCalendarData === void 0 ? void 0 : memoizedCalendarData.data,
    onMonthChange: month => {
      changeMonthCallback(month, [selectedDates[0], undefined]);
    },
    isMobile: isMobile
  }), memoizedCalendarData && memoizedCalendarData.data.length > 0 ? __jsx("div", {
    className: cn("flex flex-col w-full justify-start items-center gap-4", {
      "md:flex-row gap-2": !isMobile
    })
  }, __jsx(CalendarPricesFooter, {
    minWidthFitIfCan: !isMobile,
    lowestFrom: (_memoizedCalendarData = memoizedCalendarData === null || memoizedCalendarData === void 0 ? void 0 : memoizedCalendarData.lowest_from) !== null && _memoizedCalendarData !== void 0 ? _memoizedCalendarData : 0,
    highestFrom: (_memoizedCalendarData2 = memoizedCalendarData === null || memoizedCalendarData === void 0 ? void 0 : memoizedCalendarData.highest_from) !== null && _memoizedCalendarData2 !== void 0 ? _memoizedCalendarData2 : 0,
    mediumFrom: (_memoizedCalendarData3 = memoizedCalendarData === null || memoizedCalendarData === void 0 ? void 0 : memoizedCalendarData.median_from) !== null && _memoizedCalendarData3 !== void 0 ? _memoizedCalendarData3 : 0
  }), disclaimerText && __jsx("div", {
    className: "flex-1"
  }, __jsx(InformativeCard, {
    message: disclaimerText,
    severity: "info",
    className: "w-[270px] m-auto tx-pxs-light text-primary_text"
  }))) : null, __jsx("div", {
    className: cn("flex flex-col w-full justify-between", {
      "md:flex-row": !isMobile
    })
  }, __jsx("div", {
    className: cn("flex-1 pt-[1em] flex flex-row justify-center items-center gap-2 w-full", {
      "md:pr-[1em]": !isMobile
    })
  }, __jsx(MuscleFormDateInput, {
    className: "tx-pmd-regular text-neutral_black",
    value: selectedDates[0] ? selectedDates[0].toISOString() : undefined,
    minDate: minDate.toISOString(),
    maxDate: selectedDates[1] ? selectedDates[1].toISOString() : undefined,
    onChange: date => handleFromDateChange(date ? new Date(date) : undefined),
    fullLength: true,
    hideEmptyLabel: true,
    hideGuideText: true,
    hideErrorMessage: true,
    dataTestId: dataTestId + "-from-date-input"
  }), dateRangeMode ? __jsx(React.Fragment, null, __jsx("p", {
    className: "text-neutral_black tx-psm-regular"
  }, t('Shared.TO_THE')), __jsx(MuscleFormDateInput, {
    className: "tx-pmd-regular text-primary_text",
    value: selectedDates[1] ? selectedDates[1].toISOString() : undefined,
    minDate: selectedDates[0] ? selectedDates[0].toISOString() : undefined,
    maxDate: maxDate === null || maxDate === void 0 ? void 0 : maxDate.toISOString(),
    onChange: date => handleToDateChange(date ? new Date(date) : undefined),
    fullLength: true,
    hideGuideText: true,
    hideEmptyLabel: true,
    hideErrorMessage: true,
    dataTestId: dataTestId + "-to-date-input"
  })) : null), __jsx("div", {
    className: cn("flex-1 pt-[1em]", {
      "md:pl-[1em]": !isMobile
    })
  }, applyImmediate || isMobile ? __jsx(MuscleButton, {
    dataTestId: dataTestId + "-choose-dates-button",
    onClick: () => {
      toggleCalendar(false);
    },
    disabled: dateRangeMode && (value[1] === undefined || value[0] === undefined) || !dateRangeMode && value[0] === undefined,
    variant: "solidXL"
  }, t('Shared.CHOOSE_DATES')) : __jsx("div", {
    className: "flex justify-end items-center gap-4"
  }, __jsx(MuscleButton, {
    dataTestId: dataTestId + "-cancel-button",
    onClick: () => {
      onChangeValue({
        from: undefined,
        to: undefined
      });
      toggleCalendar(false);
    },
    variant: "outlineSm"
  }, t('Shared.CANCEL')), __jsx(MuscleButton, {
    dataTestId: dataTestId + "-choose-dates-button",
    onClick: () => {
      onChangeValue({
        from: selectedDates[0],
        to: selectedDates[1]
      });
      toggleCalendar(false);
    },
    variant: "solidSm",
    disabled: dateRangeMode && (selectedDates[1] === undefined || selectedDates[0] === undefined) || !dateRangeMode && selectedDates[0] === undefined
  }, t('Shared.CHOSE_DATES_SMALL'))))))));
};
export default MuscleCalendarDateInput;